import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { Observable, Subject } from 'rxjs';
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChange,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { takeUntil } from 'rxjs/operators';
import { ActiveElement, Chart, ChartData, ChartEvent, ChartOptions, ChartType } from 'chart.js';
import { IChartClickEvent } from '@fi-shared/fi-chart/fi-chart';

@Component({
    selector: 'fi-chart',
    templateUrl: './fi-chart.component.html',
    styleUrls: ['./fi-chart.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-chart' },
    standalone: false,
})
export class FiChartComponent implements OnInit, OnChanges, OnDestroy {
    @Input() data: any[]; // number[] or number[][]
    @Input() labels: string[];
    @Input() series: string[];
    @Input() colors: string[];
    @Input() options: ChartOptions<'doughnut' | 'bar' | 'line'>;
    @Input() chartType: ChartType;
    @Input() legend: boolean;
    @Input() isCustomLegend: boolean;
    @Input() recalculateChartWidth: Observable<void> = new Observable<void>();
    @Input() isCursorPointer: boolean = true;
    @Output() chartClick = new EventEmitter<IChartClickEvent>();

    @ViewChild('chart', { static: false }) chart: BaseChartDirective;
    @ViewChild('chartWrapper', { static: true }) chartWrapper: ElementRef;
    @ViewChild('chartLegendContainer', { static: false }) chartLegendContainer: ElementRef;
    internalData: ChartData;
    minChartColumnWidthPx = 25;

    constructor(
        private renderer: Renderer2,
        private elementRef: ElementRef,
        private detectDevicesService: DetectDevicesService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        const hasChanges = (change: SimpleChange) => change && !change.firstChange;
        if (
            hasChanges(changes.data) ||
            hasChanges(changes.labels) ||
            hasChanges(changes.series) ||
            hasChanges(changes.colors)
        )
            this.reloadChart();
    }

    ngOnInit() {
        this.buildData();

        this.recalculateChartWidth.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            // NOTE: setTimeout() because of container's width has not been changed yet
            setTimeout(() => this.reloadChart());
        });
    }

    setCursorPointer() {
        this.options.onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
            this.renderer.setStyle(chart.canvas, 'cursor', elements.length > 0 ? 'pointer' : 'default');
        };
    }

    onChartClick($event) {
        this.chartClick.emit($event);
    }

    private buildData() {
        if (this.series) {
            const colors = this.getColorsByCount(this.series.length, this.colors || this.defaultColors);
            this.internalData = {
                datasets: this.series.map((serie, idx) => {
                    const color = colors[idx];
                    const hoverColor = this.applyAlphaToColor(color, 0.6);
                    return {
                        data: this.data[idx].slice(),
                        label: serie,
                        borderColor: color,
                        backgroundColor: color,
                        hoverBackgroundColor: hoverColor,
                        pointBorderColor: color,
                        pointBackgroundColor: color,
                        pointHoverBackgroundColor: hoverColor,
                    };
                }),
                labels: this.labels,
            };
        } else {
            this.internalData = {
                datasets: [
                    {
                        data: this.data.slice(),
                        label: '',
                        backgroundColor: this.getColorsByCount(this.data.length, this.colors || this.defaultColors),
                        tension: 0.4,
                    },
                ],
                labels: this.labels,
            };
            setTimeout(() => {
                this.setScrollable();
            });
        }
        if (this.isCursorPointer) this.setCursorPointer();

        if (this.isCustomLegendShown()) setTimeout(() => this.setCustomLegendStyle());
    }

    isCustomLegendShown() {
        return this.isCustomLegend && this.chartType === 'doughnut' && !this.detectDevicesService.isMobileSmall();
    }

    setCustomLegendStyle() {
        /*const legendList = this.chartLegendContainer.nativeElement.children[0];

        Array.from<HTMLElement>(legendList.children).forEach((legendListItem, index) => {
            if (legendListItem.children[0].textContent)
                this.chartLegendColors.push(legendListItem.children[0].textContent);

            let legendListColorItem = legendListItem.children[0]; 
            this.renderer.setStyle(legendListItem, 'margin-bottom', '5px');
            this.renderer.setStyle(legendListColorItem, 'background-color', this.chartLegendColors[index]); 
            this.renderer.setStyle(legendListColorItem, 'width', '60px');
            this.renderer.setStyle(legendListColorItem, 'height', '18px');
            this.renderer.setStyle(legendListColorItem, 'display', 'inline-block');
            this.renderer.setStyle(legendListColorItem, 'vertical-align', 'middle');
            legendListColorItem.textContent = '';
        });

        this.renderer.setStyle(legendList, 'list-style', 'none');
        this.renderer.setStyle(this.elementRef.nativeElement, 'height', this.chartWrapper.nativeElement.height);
        this.renderer.setStyle(this.elementRef.nativeElement, 'display', 'flex');
        this.renderer.setStyle(this.elementRef.nativeElement, 'justify-content', 'space-between');
        this.renderer.setStyle(this.chartWrapper.nativeElement, 'width', '50%');*/
        //this.chart.ngOnInit();
    }

    setScrollable() {
        if (this.chartType === 'bar' || this.chartType === 'line') {
            this.renderer.setStyle(this.chartWrapper.nativeElement, 'width', '100%');
            const dataLength = this.internalData.datasets[0]?.data?.length || 1;
            if (this.chartWrapper.nativeElement.offsetWidth < dataLength * this.minChartColumnWidthPx) {
                this.renderer.setStyle(
                    this.chartWrapper.nativeElement,
                    'width',
                    `${dataLength * this.minChartColumnWidthPx}px`,
                );
                this.renderer.setStyle(this.elementRef.nativeElement, 'display', 'block');
            }
            //this.chart.ngOnInit();
        }
    }

    private getColorsByCount(count, colors: string[]): string[] {
        const res = [];
        for (let i = 0; i < count; i++) {
            res.push(colors[i % colors.length]);
        }
        return res;
    }

    private defaultColors = [
        '#97BBCD', // blue
        '#DCDCDC', // light grey
        '#F7464A', // red
        '#46BFBD', // green
        '#FDB45C', // yellow
        '#949FB1', // grey
        '#4D5360', // dark grey
    ];

    private applyAlphaToColor(hex, alpha) {
        hex = hex.replace(/^#/, '');
        const num = parseInt(hex, 16);
        const color = [num >> 16, (num >> 8) & 255, num & 255].map((val) => val);

        return 'rgba(' + color[0] + ', ' + color[1] + ', ' + color[2] + ', ' + alpha + ')';
    }

    private reloadChart() {
        // chart js updating works strange in current version of chart js
        // can throw Cannot read property '0' of undefined
        // at BaseChartDirective.webpackJsonp../node_modules/ng2-charts/charts/charts.js.BaseChartDirective.updateChartData (charts.js:88)
        // at BaseChartDirective.webpackJsonp../node_modules/ng2-charts/charts/charts.js.BaseChartDirective.ngOnChanges (charts.js:36)
        if (this.chart !== undefined && this.chart.chart !== undefined) {
            this.buildData();
            // this.chart.chart.destroy();
            // this.chart.datasets = this.internalDatasets;
            // this.chart.data = this.internalData;
            // this.chart.labels = this.labels;
            // this.chart.colors = this.internalColors;
            //this.chart.ngOnInit();
        }
    }

    protected ngOnDestroy$ = new Subject<void>();

    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
